import { TYPES } from '@/libs/di/types'
import type { SealedWebSocket } from '@/libs/utilities/sealedWebSocket'
import { inject, injectable } from 'inversify'

import type { MessageUsecase } from '@/apis/usecases'
import type { MessageRepository } from '@/apis/usecases/dataAccesses'
import type {
  AnyMessage,
  AnyRealtimeMessage,
  Pagination,
  RoomID,
} from '@/types/app'

const sortMessages = (messages: readonly AnyMessage[]): readonly AnyMessage[] =>
  // eslint-disable-next-line fp/no-mutating-methods
  [...messages].sort(
    (one, theOther) => one.createdAt.getTime() - theOther.createdAt.getTime()
  )

@injectable()
export class MessageInteractor implements MessageUsecase {
  @inject(TYPES.MessageRepository) private _messageRepository: MessageRepository

  public async getRoomMessages(
    roomId: RoomID,
    entranceGUID: string,
    params?: Pagination
  ): Promise<WithPagination<AnyMessage>> {
    const { data, pageInfo } = await this._messageRepository.getRoomMessages(
      roomId,
      entranceGUID,
      params
    )
    return { data: sortMessages(data), pageInfo }
  }

  public sendTypingState(socket: SealedWebSocket, isTyping: boolean) {
    if (!socket.isOpened) {
      console.info("didn't send typing state because socket is not opened")
      return
    }
    this._messageRepository.broadcastTypingState(socket, isTyping)
  }

  public async getMemberRoomMessages(
    roomId: RoomID,
    entranceGUID: string,
    params?: Pagination
  ) {
    const { data, pageInfo } =
      await this._messageRepository.getMemberRoomMessages(
        roomId,
        entranceGUID,
        params
      )
    return { data: sortMessages(data), pageInfo }
  }

  public async updateLatestReadMessage(
    roomId: RoomID,
    entranceGUID: string,
    messageGUID: string
  ) {
    return await this._messageRepository.updateLatestReadMessage(
      roomId,
      entranceGUID,
      messageGUID
    )
  }

  public createMessageWebsocket(
    roomId: RoomID,
    entranceGUID: string,
    onOpen: (event: Event) => void,
    onMessage: (message: AnyRealtimeMessage) => void,
    onClose: (event: CloseEvent) => void,
    onError: (event: Event) => void
  ): SealedWebSocket {
    return this._messageRepository.getMessagingWebSocket(
      roomId,
      entranceGUID,
      onOpen,
      onMessage,
      onClose,
      onError
    )
  }
}
